import React, { useMemo } from 'react';

const DAYS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

/**
 * Mapa de calor de atención: día de la semana × hora del día.
 *
 * Responde una pregunta que ninguna otra gráfica responde: *cuándo* trabaja
 * realmente el médico. Sirve para decidir dónde abrir cupos, qué franjas están
 * muertas y si se está atendiendo fuera de horario.
 *
 * Se dibuja con CSS grid y no con una librería de gráficos porque es una
 * cuadrícula de celdas de color: meter un motor de charts aquí solo añadiría
 * peso y menos control sobre la accesibilidad.
 *
 * @param {Array<{dia:number, hora:number, consultas:number}>} data - `dia` 0=lunes
 * @param {number} [startHour=6] - Primera hora que se muestra
 * @param {number} [endHour=21] - Última hora que se muestra
 */
export default function HourlyHeatmap({ data = [], startHour = 6, endHour = 21 }) {
  const { grid, max, hours } = useMemo(() => {
    const map = new Map();
    let maxValue = 0;

    // El rango visible se estira si hay atención fuera de él: es mejor mostrar
    // una consulta a las 23:00 que ocultarla, justamente porque es la anómala.
    let from = startHour;
    let to = endHour;
    data.forEach(({ hora }) => {
      if (hora < from) from = hora;
      if (hora > to) to = hora;
    });

    data.forEach(({ dia, hora, consultas }) => {
      map.set(`${dia}-${hora}`, consultas);
      if (consultas > maxValue) maxValue = consultas;
    });

    return {
      grid: map,
      max: maxValue,
      hours: Array.from({ length: to - from + 1 }, (_, i) => from + i),
    };
  }, [data, startHour, endHour]);

  if (!data.length) {
    return <p className="text-sm text-slate-500 py-8 text-center">Sin consultas en este rango</p>;
  }

  /** Intensidad 0-1 sobre raíz cuadrada: sin esto, un pico aplana todo lo demás */
  const intensity = (value) => (max <= 0 ? 0 : Math.sqrt(value / max));

  return (
    <div className="overflow-x-auto">
      <div className="inline-block min-w-full">
        <div className="grid gap-[2px]" style={{ gridTemplateColumns: `auto repeat(${hours.length}, minmax(18px, 1fr))` }}>
          <div />
          {hours.map(hour => (
            <div key={hour} className="text-[10px] text-slate-400 text-center pb-1">
              {hour % 3 === 0 ? `${hour}h` : ''}
            </div>
          ))}

          {DAYS.map((label, dayIndex) => (
            <React.Fragment key={label}>
              <div className="text-[11px] text-slate-500 pr-2 flex items-center justify-end">{label}</div>
              {hours.map(hour => {
                const value = grid.get(`${dayIndex}-${hour}`) || 0;
                const alpha = intensity(value);
                return (
                  <div
                    key={`${dayIndex}-${hour}`}
                    title={`${label} ${hour}:00 — ${value} ${value === 1 ? 'consulta' : 'consultas'}`}
                    className="aspect-square rounded-[2px] border border-slate-100"
                    style={{
                      backgroundColor: value ? `rgba(37, 99, 235, ${0.12 + alpha * 0.88})` : '#f8fafc',
                    }}
                  />
                );
              })}
            </React.Fragment>
          ))}
        </div>

        <div className="flex items-center gap-2 mt-3 text-[11px] text-slate-500">
          <span>Menos</span>
          {[0, 0.25, 0.5, 0.75, 1].map(step => (
            <div key={step} className="w-4 h-4 rounded-[2px] border border-slate-100"
                 style={{ backgroundColor: step ? `rgba(37, 99, 235, ${0.12 + step * 0.88})` : '#f8fafc' }} />
          ))}
          <span>Más</span>
          <span className="ml-2">· máximo {max} en una hora</span>
        </div>
      </div>
    </div>
  );
}
